import BaseModal from "@components/ui/modals/base-modal/BaseModal";
import type { Cart, Product as ProductModel, ProductVariant } from "@graphql/generated/types";
import dynamic from "next/dynamic";
import { memo } from "react";
import { Bar } from "./components/button-bar";
import { useTryAndBuyModal } from "./hooks/useOpenTryAndBuyModal";
import { useWarning } from "./hooks/useWarning";
import { AvailabilityProvider } from "./providers/availability";
import { ContextProvider } from "./providers/context";
import { StoresProvider } from "./providers/stores";
import { styles } from "./TryAndBuy.style";

const MainModalContent = dynamic(
    () => import("@components/try-and-buy/components/main-modal-content")
);

type Props = {
    product: ProductModel;
    selectedVariant: ProductVariant;
    currentCart?: Cart;
};

const propsAreEqual = (prev: Props, next: Props) =>
    prev.product.id === next.product.id &&
    prev.selectedVariant?.sku === next.selectedVariant?.sku &&
    prev.currentCart?.lineItems?.length === next.currentCart?.lineItems?.length;

export const TryAndBuy = memo(function TryAndBuy({ product, selectedVariant, currentCart }: Props) {
    const { onOpen, isOpen, label, onClose } = useTryAndBuyModal();
    const { ToastContainer, showWarning } = useWarning({ onOpen });

    const onClickTryAndBuy = currentCart && currentCart?.lineItems?.length ? showWarning : onOpen;

    return (
        <ContextProvider isOpen={isOpen} product={product} selectedVariant={selectedVariant}>
            <StoresProvider>
                <BaseModal
                    isOpen={isOpen}
                    onClose={onClose}
                    label={label}
                    customStyle={{
                        content: styles.modalContent,
                    }}
                >
                    <MainModalContent onOpen={onOpen} onClose={onClose} />
                </BaseModal>
                <ToastContainer />

                <AvailabilityProvider selectedVariant={selectedVariant}>
                    {({ isNotAvailable, isLoading }) => (
                        <Bar
                            isLoading={isLoading}
                            isNotAvailable={isNotAvailable}
                            onClick={onClickTryAndBuy}
                        />
                    )}
                </AvailabilityProvider>
            </StoresProvider>
        </ContextProvider>
    );
}, propsAreEqual);
